import { useEffect, useState, type FormEvent } from 'react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import type { ReferenciaBancariaAsociado } from '@/lib/typesNeoCore'

/**
 * Modal para capturar / editar la cuenta bancaria EXTERNA del asociado.
 * Se usa para retiros vía transferencia bancaria y desembolsos de crédito.
 *
 * v0: el admin captura los datos manualmente y se marcan como
 * `KYC_CONFIRMADO` (asume que cruzó contra DPI físico). ACH automático
 * es NC-93b futuro.
 */

const TIPOS = [
  { value: 'MONETARIA', label: 'Monetaria' },
  { value: 'AHORRO', label: 'Ahorro' },
] as const

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  asociadoNombre: string
  referencia: ReferenciaBancariaAsociado | null
  onSuccess: () => void
}

export function EditarReferenciaBancariaModal({
  open, onClose, asociadoId, asociadoNombre, referencia, onSuccess,
}: Props) {
  const [banco, setBanco] = useState('')
  const [tipo, setTipo] = useState<'MONETARIA' | 'AHORRO'>('MONETARIA')
  const [numero, setNumero] = useState('')
  const [titular, setTitular] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEdit = !!referencia

  useEffect(() => {
    if (!open) return
    setError(null)
    setBanco(referencia?.banco ?? '')
    setTipo((referencia?.tipo as 'MONETARIA' | 'AHORRO') ?? 'MONETARIA')
    setNumero(referencia?.numero_cuenta ?? '')
    setTitular(referencia?.titular_nombre ?? asociadoNombre)
  }, [open, referencia, asociadoNombre])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!banco.trim()) { setError('Falta el nombre del banco.'); return }
    if (!numero.trim()) { setError('Falta el número de cuenta.'); return }
    if (!titular.trim()) { setError('Falta el nombre del titular.'); return }

    // Si el titular no coincide con el nombre del asociado, advertencia
    const titularUpper = titular.trim().toUpperCase()
    const asociadoUpper = asociadoNombre.toUpperCase()
    const sospechoso = !titularUpper.includes(asociadoUpper.split(' ')[0]) &&
                       !asociadoUpper.includes(titularUpper.split(' ')[0])
    if (sospechoso && !confirm(
      `El titular "${titular}" no parece coincidir con el asociado "${asociadoNombre}". ` +
      `Por norma INGECOP la cuenta debe ser del asociado mismo. ¿Confirmás que es correcto?`
    )) {
      return
    }

    setSubmitting(true)
    const { error: rpcErr } = await supabase.rpc('actualizar_referencia_bancaria_asociado', {
      p_input: {
        asociado_id: asociadoId,
        banco: banco.trim(),
        numero_cuenta: numero.trim(),
        tipo,
        moneda: 'GTQ',
        titular_nombre: titular.trim(),
        validacion_metodo: 'KYC_CONFIRMADO',
      },
    })
    setSubmitting(false)

    if (rpcErr) { setError(`No se pudo guardar: ${rpcErr.message}`); return }

    onSuccess()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Editar cuenta bancaria del asociado' : 'Cuenta bancaria del asociado'}
      subtitle={asociadoNombre}
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="ref-bancaria-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Guardando…' : (isEdit ? 'Guardar cambios' : 'Agregar cuenta')}
          </button>
        </>
      }
    >
      <form id="ref-bancaria-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <div
          style={{
            padding: '12px 14px', background: 'var(--brand-50)',
            border: '1px solid var(--brand-100, var(--brand-200))',
            borderRadius: 10, fontSize: 12, color: 'var(--ink-soft)',
            lineHeight: 1.55,
          }}
        >
          Esta cuenta se usa para enviar transferencias al asociado (retiros, desembolsos
          de crédito). <strong>El titular debe coincidir con el asociado</strong> — norma INGECOP.
        </div>

        <Field label="Banco">
          <input value={banco} onChange={(e) => setBanco(e.target.value.toUpperCase())} placeholder="BANRURAL" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Tipo de cuenta">
            <select value={tipo} onChange={(e) => setTipo(e.target.value as 'MONETARIA' | 'AHORRO')}>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="N° de cuenta">
            <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="4424158276" />
          </Field>
        </div>

        <Field label="Nombre del titular (según banco)">
          <input value={titular} onChange={(e) => setTitular(e.target.value.toUpperCase())} placeholder={asociadoNombre} />
          <span style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 4 }}>
            Debe coincidir exactamente con el registro bancario.
          </span>
        </Field>
      </form>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="form-label" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}
